import { useEffect, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import SignaturePad from "signature_pad";
import { StyledDialog } from "src/components/modal/senec-dialog/SenecDialog.styled";
import { SenecDialogContent } from "src/components/modal/senec-dialog/SenecDialogContent";
import { SenecSignatureDialogButtons } from "src/components/inputs/senec-signature/SenecSignatureDialogButtons";
import { trimAndResizeSignature } from "src/components/inputs/senec-signature/signatureSvgUtils";

export interface SenecSignatureDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onReturn: () => void;
    title: string;
    clearButtonText: string;
    saveButtonText: string;
    onSave: (signatureSvg: string) => void;
    dataTestId: string;
    signatureMaxHeight?: number;
    signatureMaxWidth?: number;
}

export const SenecSignatureDialog = (props: SenecSignatureDialogProps) => {
    const [signatureRef, setSignatureRef] = useState<SignatureCanvas | null>(null);
    const [signatureDataPoints, setSignatureDataPoints] = useState<SignaturePad.Point[][] | null>(null);
    const [numOfStrokes, setNumOfStrokes] = useState<number>(0);

    useEffect(() => {
        if (signatureDataPoints != null && signatureRef) {
            signatureRef.fromData(signatureDataPoints);
            setNumOfStrokes(signatureDataPoints.length);
        }
    }, [signatureRef]);

    const onClear = () => {
        signatureRef?.clear();
    };

    const beforeClose = () => {
        onClear();
        setSignatureDataPoints(signatureDataPoints?.slice(0, numOfStrokes) ?? []);
    };

    const onClose = () => {
        beforeClose();
        props.onClose();
    };

    const onReturn = () => {
        beforeClose();
        props.onReturn();
    };

    const onSave = () => {
        if (signatureRef) {
            if (signatureRef.isEmpty()) {
                setSignatureDataPoints(null);
                props.onSave("");
                return;
            }
            const svg = signatureRef.getSignaturePad()._toSVG();
            props.onSave(trimAndResizeSignature(svg, props.signatureMaxHeight, props.signatureMaxWidth));
            setSignatureDataPoints(signatureRef.toData());
        }
    };

    return (
        <StyledDialog open={props.isOpen} onClose={props.onClose} fullScreen>
            <SenecDialogContent
                data-testid={props.dataTestId}
                title={props.title}
                MainComponent={
                    <SignatureCanvas
                        canvasProps={{ style: { width: "100%", height: "100%", display: "block" } }}
                        ref={(ref: SignatureCanvas | null) => {
                            setSignatureRef(ref);
                        }}
                        clearOnResize={false}
                        data-testid={"signature-dialog-canvas"}
                    />
                }
                BottomComponent={
                    <SenecSignatureDialogButtons
                        clearButtonText={props.clearButtonText}
                        saveButtonText={props.saveButtonText}
                        onClear={onClear}
                        onSave={onSave}
                        data-testid={"signature-dialog-action-buttons"}
                    />
                }
                onClose={onClose}
                onReturn={onReturn}
                showDivider={false}
                showLoader={false}
                fillHeight
            />
        </StyledDialog>
    );
};
